// Video metadata extraction utilities using Pyodide + Python OpenCV

let pyodide = null;

// Initialize Pyodide and install OpenCV
const initPyodide = async () => {
  if (pyodide) return pyodide;
  
  try {
    // Load Pyodide from CDN
    const pyodideScript = document.createElement('script');
    pyodideScript.src = 'https://cdn.jsdelivr.net/pyodide/v0.24.1/full/pyodide.js';
    document.head.appendChild(pyodideScript);
    
    // Wait for Pyodide to load
    await new Promise((resolve, reject) => {
      pyodideScript.onload = () => {
        resolve();
      };
      pyodideScript.onerror = (error) => {
        console.error('Failed to load Pyodide script:', error);
        reject(error);
      };
    });
    
    // Initialize Pyodide with silent logging
    pyodide = await window.loadPyodide({
      stdout: () => {}, // Suppress stdout
      stderr: () => {}, // Suppress stderr
    });
    
    // Install OpenCV
    await pyodide.loadPackage(['opencv-python', 'numpy']);
    
    return pyodide;
  } catch (error) {
    console.error('Failed to load Pyodide + OpenCV:', error);
    throw new Error(`Pyodide initialization failed: ${error.message}`);
  }
};

// Extract frame rate from video file using Python OpenCV
export const getVideoFrameRate = async (videoFile) => {
  try {
    const pyodideInstance = await initPyodide();
    
    // Convert JavaScript file to Python bytes
    const arrayBuffer = await videoFile.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    
    // Write file to Pyodide's virtual filesystem
    const fileName = `/tmp/${videoFile.name}`;
    pyodideInstance.FS.writeFile(fileName, uint8Array);
    
    // Run Python OpenCV code (same as your desktop Python)
    const pythonCode = `
import cv2
import numpy as np

# Open video file with OpenCV (exactly like desktop Python)
cap = cv2.VideoCapture('${fileName}')

if not cap.isOpened():
    raise Exception("Could not open video file")

# Get frame rate using cv2.CAP_PROP_FPS (same as desktop)
fps = cap.get(cv2.CAP_PROP_FPS)

# Get additional video properties
frame_count = cap.get(cv2.CAP_PROP_FRAME_COUNT)
width = cap.get(cv2.CAP_PROP_FRAME_WIDTH)
height = cap.get(cv2.CAP_PROP_FRAME_HEIGHT)
duration = frame_count / fps if fps > 0 else 0

# Clean up
cap.release()

# Return the frame rate
fps
`;
    
    // Execute Python code
    const fps = pyodideInstance.runPython(pythonCode);
    
    // Clean up file
    pyodideInstance.FS.unlink(fileName);
    
    if (fps && fps > 0 && isFinite(fps)) {
      return fps;
    } else {
      throw new Error('Python OpenCV could not detect frame rate');
    }
    
  } catch (error) {
    console.error('Pyodide OpenCV frame rate detection error:', error);
    throw new Error(`Python OpenCV frame rate detection failed: ${error.message}`);
  }
};

// Get video metadata including duration, dimensions, and Python OpenCV frame rate
export const getVideoMetadata = async (videoFile) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const objectUrl = URL.createObjectURL(videoFile);
    
    video.addEventListener('loadedmetadata', async () => {
      try {
        // Use Python OpenCV for frame rate detection
        const fps = await getVideoFrameRate(videoFile);
        
        const metadata = {
          duration: video.duration,
          width: video.videoWidth,
          height: video.videoHeight,
          fps: fps,
          fileSize: videoFile.size,
          fileName: videoFile.name,
          fileType: videoFile.type
        };
        
        URL.revokeObjectURL(objectUrl);
        resolve(metadata);
      } catch (error) {
        URL.revokeObjectURL(objectUrl);
        reject(error);
      }
    });
    
    video.addEventListener('error', (error) => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load video metadata'));
    });
    
    video.src = objectUrl;
    video.load();
  });
};